import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {isEmpty} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import LFCChartsInputScaleDialog, {
  initialScaleValue,
  ScaleValue
} from './components/LFCChartsInputScale';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  x?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  y?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  yAxisName?: string;
  series?: string;
  stacked?: boolean; //付与する事で積み上げになります
  grid?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  color?: string | {};
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  height?: string;
  bottom?: string;
  onClick?: (prm: {data: any}) => void;
  mapName?: string;
  xlist?: any;
  inputScale?: boolean;
}

/**
 * LFCChartsBar
 * @param props
 * @returns
 */
const LFCChartsBoxSearchByMonth = (props: Props) => {
  const {t} = useTranslation();
  const [scaleValue, setScaleValue] = useState<ScaleValue>(initialScaleValue);
  const [inputScaleOpen, setInputScaleOpen] = useState(false);
  const [myGrid, setMyGrid] = useState({
    top: 65,
    left: 60,
    right: 65,
    bottom: 40
  });

  // スケール変更
  const onApply = (scaleValue: ScaleValue) => {
    setScaleValue(scaleValue);
  };

  const makeBoxData = () => {
    //ボックスデータの算出
    const CalcQuartile = (data: any) => {
      if (!isEmpty(data)) {
        let max = Math.max(...data.map((a: any) => a));
        let min = Math.min(...data.map((a: any) => a));

        const dataQ1 = () => {
          let data_s: any = data.sort((a: number, b: number) => a - b);
          let pos = (data_s.length - 1) * 0.25;
          let base = Math.floor(pos);
          let rest = pos - base;
          if (data_s[base + 1] !== undefined) {
            return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
          } else {
            return data_s[base];
          }
        };

        const dataQ2 = () => {
          let data_s: any = data.sort((a: number, b: number) => a - b);
          let pos = (data_s.length - 1) * 0.5;
          let base = Math.floor(pos);
          let rest = pos - base;
          if (data_s[base + 1] !== undefined) {
            return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
          } else {
            return data_s[base];
          }
        };

        const dataQ3 = () => {
          let data_s: any = data.sort((a: number, b: number) => a - b);
          let pos = (data_s.length - 1) * 0.75;
          let base = Math.floor(pos);
          let rest = pos - base;
          if (data_s[base + 1] !== undefined) {
            return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
          } else {
            return data_s[base];
          }
        };
        // [min,  Q1,  median (or Q2),  Q3,  max]
        return [min, dataQ1(), dataQ2(), dataQ3(), max];
      }
    };

    let tmp: any = [];
    props.xlist.forEach((a: string) => {
      if (props.source.x_list.find((k: string) => k === a)) {
        props.source.x_list.find((b: string, index: number) => {
          if (b === a) {
            tmp.push(props.source.data_value[index]);
            return true;
          }
          return false;
        });
      } else {
        tmp.push([]);
      }
    });

    let tmpOutPut: any = [];
    tmp.forEach((a: any) => {
      if (!isEmpty(a)) {
        tmpOutPut.push(CalcQuartile(a));
      } else {
        tmpOutPut.push([]);
      }
    });
    return tmpOutPut;
  };

  useEffect(() => {
    if (!props.grid) return;

    let newGrid: any = myGrid;
    Object.entries(props.grid).forEach(([key, value]: [string, number]) => {
      if (value) newGrid[key] = value;
    });
    setMyGrid(newGrid);
  }, [props.grid]);

  return (
    <div>
      <LFCBaseCharts
        option={{
          title: {
            text: props.title
          },
          tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
              if (params.seriesType === 'boxplot') {
                // [min,  Q1,  median (or Q2),  Q3,  max]
                return (
                  params.name +
                  '<br/>' +
                  t('最大') +
                  ':' +
                  params.value[5].toFixed(2) +
                  '<br/>' +
                  'Q3：' +
                  params.value[4].toFixed(2) +
                  '<br/>' +
                  t('中央') +
                  ' (or Q2)：' +
                  params.value[3].toFixed(2) +
                  '<br/>' +
                  'Q1：' +
                  params.value[2].toFixed(2) +
                  '<br/>' +
                  t('最小') +
                  ':' +
                  params.value[1].toFixed(2)
                );
              } else if (params.seriesType === 'scatter') {
                return params.name + '<br/>' + t('はずれ値') + ':' + params.value[1].toFixed(2);
              }
            }
          },
          dataZoom: [
            {
              type: 'inside'
            }
          ],
          grid: myGrid,
          xAxis: {
            type: 'category',
            data: props.xlist,
            // splitLine: {
            //   show: false
            // },
            axisLabel: {
              // interval: 0,
              rotate: '45',
              // fontSize: 10
              width: myGrid.bottom,
              overflow: 'truncate'
            }
          },
          yAxis: {
            type: 'value',
            name: props.yAxisName !== undefined ? props.yAxisName : '',
            nameTextStyle: {
              align: 'left'
            },
            axisLabel: {
              width: myGrid.left,
              overflow: 'truncate'
            },
            min: scaleValue.y1AxisMin,
            max: scaleValue.y1AxisMax,
            interval: scaleValue.y1AxisInterval
          },
          series: [
            {
              name: 'boxplot',
              type: 'boxplot',
              data: makeBoxData()
            }
          ]
        }}
        exportData={props.exportData}
        exportFields={props.exportFields}
        exportFilename={props.exportFilename}
        height={props.height}
        onEvents={{
          click: (params: any) => {
            if (props.onClick != null) {
              params.mapName = props.mapName;
              props.onClick(params);
            }
          }
        }}
        inputScaleStatus={props.inputScale !== undefined ? props.inputScale : false}
        inputScaleOpen={() => setInputScaleOpen(true)}
      />
      <LFCChartsInputScaleDialog
        open={inputScaleOpen}
        onClose={() => setInputScaleOpen(false)}
        onApply={onApply}
        y1AxisName={props.yAxisName}
        inputY1Axis={true}
      />
    </div>
  );
};
export default LFCChartsBoxSearchByMonth;
